import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { FolderOpen, CheckCircle2, XCircle, Trash2, AlertTriangle, RefreshCw, Plus, Search, X, Pencil, Hourglass } from "lucide-react";

// ─── Font ─────────────────────────────────────────────────────────────────────
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap";
// ─── Design tokens (identical to UserMaster) ─────────────────────────────────
const T = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red: "#C8102E",
  redLight: "#FCEAED",
  orange: "#E8640A",
  orangeLight: "#FEF0E6",
  gold: "#C87800",
  goldLight: "#FEF5DC",
  green: "#1A7A42",
  greenLight: "#E2F5EC",
  bg: "#F4F0FA",
  surface: "#FFFFFF",
  surfaceAlt: "#FAF8FD",
  dark: "#1A0A2E",
  muted: "#6B5A8A",
  faint: "#C4B8DC",
  borderSolid: "#E8E0F5",
  shadow: "0 2px 12px rgba(92,45,145,0.06)",
  shadowMd: "0 4px 20px rgba(92,45,145,0.12)",
};

// ─── Reusable input style ─────────────────────────────────────────────────────
const inputSty = (focused) => ({
  width: "100%", borderRadius: 10,
  border: `1.5px solid ${focused ? T.violet : T.borderSolid}`,
  padding: "10px 14px", fontSize: 14, color: T.dark,
  background: focused ? T.surface : T.surfaceAlt,
  outline: "none", fontFamily: "Montserrat, sans-serif",
  boxSizing: "border-box", transition: "all 0.18s",
  boxShadow: focused ? `0 0 0 3px ${T.violetLight}` : "none",
});

const LabelSty = {
  fontSize: 11, fontWeight: 700, color: T.muted,
  textTransform: "uppercase", letterSpacing: "0.07em",
  display: "block", marginBottom: 6,
};

// ─── Icon action button (same as UserMaster ActionBtn) ────────────────────────
const ActionBtn = ({ title, onClick, color, bg, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <button title={title} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        padding: "7px 16px", borderRadius: 10,
        border: `1px solid ${hov ? color : color + "40"}`,
        cursor: "pointer", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 14, flexShrink: 0,
        transition: "all 0.16s",
        background: hov ? color : bg,
        color: hov ? "#fff" : color,
        boxShadow: hov ? `0 2px 8px ${color}30` : "none",
      }}>{children}</button>
  );
};

function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError = modal.type === "error";
  const iconMap = { success: <CheckCircle2 size={22} />, error: <XCircle size={22} />, confirm: <Trash2 size={22} />, validation: <AlertTriangle size={22} /> };
  const colorMap = {
    success: { accent: T.green, light: T.greenLight, border: "#A7F3D0" },
    error: { accent: T.red, light: T.redLight, border: "#FECDD3" },
    confirm: { accent: T.red, light: T.redLight, border: "#FECDD3" },
    validation: { accent: T.orange, light: T.orangeLight, border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "rcFadeIn 0.18s ease",
    }}>
      <style>{`@keyframes rcFadeIn { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }`}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>{modal.title}</div>
              {modal.subtitle && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{modal.subtitle}</div>}
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {isConfirm && (
              <button onClick={onClose} style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                background: T.surfaceAlt, color: T.muted,
                border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
              }}>Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: (isConfirm || isError)
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
                boxShadow: `0 4px 14px ${(isConfirm || isError) ? T.red : T.violet}40`,
              }}
            >{isConfirm ? "Delete" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ─── Component ────────────────────────────────────────────────────────────────
export default function ReportCategoryMaster() {

  // ── ALL ORIGINAL STATE (UNCHANGED) ──────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [hov, setHov] = useState(null);
  const [focused, setFocused] = useState(false);
  const [modal, setModal] = useState(null);
  const showModal = (type, title, message, onConfirm = null) =>
    setModal({ type, title, message, onConfirm });
  const closeModal = () => setModal(null);
  const formRef = useRef(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const [refreshing, setRefreshing] = useState(false);

  // ── ALL ORIGINAL LOGIC (UNCHANGED) ──────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await API.get("/admin/report-categories", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setCategories(Array.isArray(res.data.data) ? res.data.data : []);
      }
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setCategories([]);
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const handleSave = async () => {
    const title = categoryTitle.trim();

    // Required
    if (!title) {
      showModal(
        "validation",
        "Validation Error",
        "Category title is required."
      );
      return;
    }

    // Only letters & spaces
    if (!/^[A-Za-z ]+$/.test(title)) {
      showModal(
        "validation",
        "Validation Error",
        "Category title can contain only letters and spaces."
      );
      return;
    }

    // Minimum Length
    if (title.length < 3) {
      showModal(
        "validation",
        "Validation Error",
        "Category title must be at least 3 characters."
      );
      return;
    }

    // Maximum Length
    if (title.length > 50) {
      showModal(
        "validation",
        "Validation Error",
        "Category title cannot exceed 50 characters."
      );
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { title };

      // AFTER
      if (editingId) {
        await API.put(`/admin/report-categories/${editingId}`, payload, config);
        await fetchCategories();  // ← fetch fresh from server instead of local update
        showModal("success", "Category Updated", "Report category updated successfully.");
     } else {
        const res = await API.post("/admin/report-categories", payload, config);
        if (res.data.success) {
          await fetchCategories();  // ← fetch fresh from server so created_at (and any other server-set fields) load immediately
          showModal("success", "Category Created", "Report category created successfully.");
        }
      }
      setCategoryTitle("");
      setEditingId(null);
      setSearch("");
      setShowForm(false);
    } catch (err) {
      console.error("Save error:", err);
      const msg = err.response?.data?.message || "Unable to save the category. Please try again.";
      const isDuplicate = msg.toLowerCase().includes("already exists");
      showModal(
        isDuplicate ? "validation" : "error",
        isDuplicate ? "Duplicate Category" : "Save Failed",
        msg
      );
    }
  };

  const handleDelete = (id) => {
    showModal(
      "confirm",
      "Delete Category",
      "Are you sure you want to delete this category? This action cannot be undone.",
      async () => {
        try {
          const token = localStorage.getItem("token");
          await API.delete(`/admin/report-categories/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          await fetchCategories();
          showModal("success", "Category Deleted", "Report category deleted successfully.");
        } catch (err) {
          console.error("Delete failed:", err);
          showModal("error", "Delete Failed", "Unable to delete the category. Please try again later.");
        }
      }
    );
  };

  const handleEdit = (cat) => {
    setCategoryTitle(cat.title);
    setEditingId(cat.id);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleReset = () => {
    setCategoryTitle("");
    setEditingId(null);
    setShowForm(false);
  };

  const showToast = (msg, type = "success") => {


  };
  const filteredCategories = Array.isArray(categories)
    ? categories.filter(c => (c.title || "").toLowerCase().includes(search.toLowerCase()))
    : [];

  // ── pagination derived values ──
  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pagedCategories = filteredCategories.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <Modal modal={modal} onClose={closeModal} />


      {/* ── DELETE CONFIRM MODAL ── */}

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={FONT_LINK} rel="stylesheet" />

      <style>{`
        .rc-root *, .rc-root *::before, .rc-root *::after {
          box-sizing: border-box;
         font-family: 'Montserrat', sans-serif !important;
        }
        .rc-row:hover    { background: ${T.violetFaint} !important; }
        .rc-pill:hover   { transform: translateY(-1px); }
        .rc-pill         { transition: all 0.15s ease !important; }
        .rc-add-btn      { transition: all 0.18s ease !important; }
        .rc-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
.rc-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.10) !important;
        }
        @keyframes rc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .rc-header    { flex-direction: column !important; gap: 14px !important; }
          .rc-hide-sm   { display: none !important; }
          .rc-page-pad  { padding: 14px 12px !important; }
        }
        @media (max-width: 480px) {
          .rc-page-pad  { padding: 12px 10px !important; }
        }
          @keyframes rc-toast-in {
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: ${T.faint}; border-radius: 4px; }
      `}</style>

      <div className="rc-root rc-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="rc-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Masters
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Report Category Master
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage report category types used across project reporting
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* Refresh */}
            <button
              onClick={() => {
                setRefreshing(true);
                setTimeout(() => {
                  window.location.reload();
                }, 700); // let the spin animation play briefly before reload
              }}
              title="Refresh"
              disabled={refreshing}
              style={{
                width: 40, height: 40, borderRadius: 11,
                border: `1.5px solid ${T.borderSolid}`,
                background: T.surface, color: T.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, cursor: refreshing ? "not-allowed" : "pointer",
                opacity: refreshing ? 0.7 : 1, transition: "opacity 0.15s",
              }}
              onMouseEnter={e => { if (!refreshing) e.currentTarget.style.opacity = "0.75"; }}
              onMouseLeave={e => { if (!refreshing) e.currentTarget.style.opacity = "1"; }}
            >
              <span style={{
                display: "inline-flex",
                animation: refreshing ? "rc-spin 0.7s linear infinite" : "none",
              }}><RefreshCw size={16} /></span>
            </button>

            {/* Add / Close */}
            <button className="rc-add-btn"
              onClick={() => { if (showForm) { handleReset(); } else { setShowForm(true); } }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                cursor: "pointer", whiteSpace: "nowrap",
              }}>
              <Plus size={16} />
              {editingId ? "Edit Category" : showForm ? "Close Form" : "Add Report"}
            </button>
          </div>
        </div>

        {/* ── KPI STRIP ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Categories", value: categories.length, icon: <FolderOpen size={20} />, color: T.violet, border: "#D8C8F5", bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)` },
            { label: "Active", value: categories.length, icon: <CheckCircle2 size={20} />, color: T.green, border: "#A7F3D0", bg: `linear-gradient(135deg,${T.greenLight},#E0FAF0)` },
          ].map(s => (
            <div key={s.label} style={{ background: T.surface, borderRadius: 16, border: `1px solid ${T.borderSolid}`, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: T.shadow, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", right: -10, top: -10, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
              <div style={{ width: 44, height: 44, borderRadius: 12, background: s.bg, border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, position: "relative" }}>
                {s.icon}
              </div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                <div style={{ fontWeight: 800, fontSize: 26, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FORM CARD ── */}
        {showForm && (
          <div ref={formRef} style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", marginBottom: 24, boxShadow: T.shadowMd, scrollMarginTop: 100 }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "22px 26px" }}>
              {/* Form header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                  {editingId ? <Pencil size={16} /> : <FolderOpen size={18} />}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                    {editingId ? "Edit Report Category" : "Create New Report"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted }}>
                    {editingId ? "Update the report below" : "Enter a title for the new report "}
                  </div>
                </div>
              </div>

              {/* Input */}
              <div style={{ maxWidth: 480, marginBottom: 20 }}>
                <label style={LabelSty}>Report Title *</label>
                <input
                  type="text"
                  maxLength={50}
                  value={categoryTitle}
                  onChange={(e) => {
                    setCategoryTitle(
                      e.target.value.replace(/\s{2,}/g, " ")
                    );
                  }}
                  onKeyDown={e => e.key === "Enter" && handleSave()}
                  placeholder="e.g. Progress Report, Quality Audit, Material Report…"
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  style={inputSty(focused)}
                />
                <div style={{ fontSize: 11, color: T.faint, marginTop: 5 }}>
                  Press Enter or click Save to submit
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleSave} style={{
                  background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                  color: "#fff", border: "none", padding: "11px 24px", borderRadius: 11,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  boxShadow: `0 4px 14px ${T.violet}40`, transition: "all 0.18s",
                }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>

                    {editingId ? "Update Category" : "Save Report"}
                  </span>
                </button>
                <button onClick={handleReset} style={{
                  background: T.surfaceAlt, color: T.muted,
                  border: `1.5px solid ${T.borderSolid}`, padding: "11px 20px",
                  borderRadius: 11, fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── TABLE CARD ── */}
        <div style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(92,45,145,0.08)" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>
            {/* Table controls */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>Existing Categories</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filteredCategories.length} of {categories.length} categories
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              {/* Search */}
              <div className="rc-search" style={{ display: "flex", alignItems: "center", gap: 8, background: T.surfaceAlt, border: `1.5px solid ${T.borderSolid}`, borderRadius: 12, padding: "9px 14px", minWidth: 260, transition: "all 0.2s" }}>
                <span style={{ color: T.faint, display: "inline-flex" }}><Search size={15} /></span>
                <input type="text" placeholder="Search categories…" value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ border: "none", background: "transparent", fontSize: 13, color: T.dark, outline: "none", width: "100%", fontWeight: 400 }} />
                {search && (
                  <button onClick={() => setSearch("")} style={{ background: T.violetLight, border: "none", color: T.violet, cursor: "pointer", width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={11} /></button>
                )}
              </div>
            </div>

            {/* Loading */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "56px 0", color: T.muted, fontSize: 14 }}>
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><Hourglass size={32} color={T.violet} /></div>
                Loading categories…
              </div>

            ) : filteredCategories.length === 0 ? (
              /* Empty state */
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: `2px dashed ${T.faint}`, display: "flex", alignItems: "center", justifyContent: "center" }}><FolderOpen size={28} color={T.violet} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark, marginBottom: 6 }}>
                    {search ? "No matching categories" : "No categories yet"}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, maxWidth: 300, lineHeight: 1.6 }}>
                    {search ? `No results for "${search}". Try a different search.` : "Click '+ Add Category' to create the first one."}
                  </div>
                  {search && (
                    <button onClick={() => setSearch("")} style={{ marginTop: 14, background: `linear-gradient(135deg,${T.violet},${T.violetMid})`, color: "#fff", border: "none", padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      Clear Search
                    </button>
                  )}
                </div>
              </div>

            ) : (
              /* Table */
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 480 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {["#", "Category Title", "Created", "Actions"].map((h, hi) => (
                        <th key={h} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 18px", textAlign: hi === 0 ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop: `1px solid ${T.borderSolid}`,
                          background: T.violetFaint, whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCategories.map((cat, i) => {
                      const globalIndex = (safePage - 1) * PER_PAGE + i;
                      const isLast = i === pagedCategories.length - 1;
                      return (
                        <tr key={cat.id} className="rc-row"
                          onMouseEnter={() => setHov(cat.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s" }}>

                          {/* # */}
                          <td style={{ padding: "15px 18px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: T.violetFaint, fontWeight: 700, fontSize: 11, color: T.muted }}>
                              {globalIndex + 1}
                            </span>
                          </td>
                          {/* Title */}
                          <td style={{ padding: "15px 18px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

                              <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: T.dark }}>{cat.title}</div>
                                {/* <span style={{ fontSize: 10, fontWeight: 700, color: T.violet, background: T.violetLight, padding: "2px 8px", borderRadius: 20, display: "inline-block", marginTop: 3 }}>
                                  Report Category
                                </span> */}
                              </div>
                            </div>
                          </td>

                          {/* Created date (if available) */}
                          <td className="rc-hide-sm" style={{ padding: "15px 18px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                              {cat.created_at
                                ? new Date(cat.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                : "—"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "15px 18px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <ActionBtn title="Edit Category" onClick={() => handleEdit(cat)} color={T.violetMid} bg={T.violetLight}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                              </ActionBtn>
                              <ActionBtn title="Delete Category" onClick={() => handleDelete(cat.id)} color={T.red} bg={T.redLight}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                              </ActionBtn>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Footer count + pagination */}
            {filteredCategories.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderSolid}`, flexWrap: "wrap", gap: 12 }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  Showing{" "}
                  <strong style={{ color: T.dark }}>
                    {Math.min((safePage - 1) * PER_PAGE + 1, filteredCategories.length)}–{Math.min(safePage * PER_PAGE, filteredCategories.length)}
                  </strong>
                  {" "}of{" "}
                  <strong style={{ color: T.dark }}>{filteredCategories.length}</strong>
                  {" "}categories
                </span>

                {totalPages > 1 && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      style={{
                        height: 34, padding: "0 14px", borderRadius: 10,
                        border: `1.5px solid ${safePage === 1 ? T.borderSolid : T.violet}`,
                        background: safePage === 1 ? T.surface : T.violetLight,
                        color: safePage === 1 ? T.faint : T.violet,
                        cursor: safePage === 1 ? "not-allowed" : "pointer",
                        fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                      }}
                    >‹ Prev</button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .slice(Math.max(0, safePage - 3), Math.min(totalPages, safePage + 2))
                      .map((n) => (
                        <button key={n} onClick={() => setPage(n)} style={{
                          width: 34, height: 34, borderRadius: 10,
                          border: `1.5px solid ${n === safePage ? T.violet : T.borderSolid}`,
                          background: n === safePage ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                          color: n === safePage ? "#fff" : T.muted,
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          boxShadow: n === safePage ? `0 2px 8px ${T.violet}40` : "none",
                          transition: "all 0.15s",
                        }}>{n}</button>
                      ))}

                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      style={{
                        height: 34, padding: "0 14px", borderRadius: 10,
                        border: `1.5px solid ${safePage === totalPages ? T.borderSolid : T.violet}`,
                        background: safePage === totalPages ? T.surface : T.violetLight,
                        color: safePage === totalPages ? T.faint : T.violet,
                        cursor: safePage === totalPages ? "not-allowed" : "pointer",
                        fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                      }}
                    >Next ›</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}